import React, { useState, useCallback, useEffect } from 'react';
import { Avatar } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import {
  RedoOutlined,
  PlusCircleOutlined,
  TeamOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { IClient } from '../../shared/interfaces';
import {
  ATable,
  MenuOptions,
  StatusActive,
  OptionsClient,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';

import { TextTableClient } from './styles';
import { useConfiguration } from '../../hooks/configuration';
import { getClients } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Clients: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [clients, setClients] = useState<IClient[]>([]);
  const { configuration } = useConfiguration();
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const [search, setSearch] = useState<string>('');

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: IClient[]) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows,
      );
    },
  };

  const listClients = useCallback(async () => {
    setLoading(true);
    setClients([]);
    try {
      const response = await getClients(limit, (page - 1) * limit, search);
      setPages(response.count);
      setClients(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('clientsPage.toast.errorTitle'),
        description: t('clientsPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, t]);

  const renderColumns = () => {
    const columns = [
      {
        title: t('clientsPage.table.id'),
        dataIndex: 'code',
        width: 70,
      },
      {
        title: t('clientsPage.table.type'),
        dataIndex: 'is_supplier',
        width: 70,
        render: (value: boolean) => (
          <Avatar
            size={30}
            style={{ background: !value ? '#87d068' : '#03a2ae' }}
          >
            {!value ? 'C' : 'F'}
          </Avatar>
        ),
      },
      {
        title: t('clientsPage.table.document'),
        dataIndex: 'cpf_cnpj',
        render: (value: string) => <TextTableClient>{value}</TextTableClient>,
        width: 150,
      },
      {
        title: t('clientsPage.table.name'),
        dataIndex: 'corporate_name',
        render: (value: string) => <TextTableClient>{value}</TextTableClient>,
        width: 290,
      },
      {
        title: t('clientsPage.table.phone'),
        dataIndex: 'cell_phone',
        render: (value: string) => <TextTableClient>{value}</TextTableClient>,
        width: 140,
      },
      {
        title: t('clientsPage.table.status'),
        dataIndex: 'status',
        width: 80,
        render: (value: boolean) => <StatusActive status={value} />,
      },
      {
        title: t('clientsPage.table.options'),
        width: 135,
        render: (cell: IClient) => <OptionsClient client={cell} />,
      },
    ];

    if (configuration?.show_client_code) {
      columns.splice(1, 0, {
        title: t('clientsPage.table.code'),
        dataIndex: 'client_code',
        width: 100,
      });
    }
    return columns;
  };

  useEffect(() => {
    listClients();
  }, [page, search, limit, listClients]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('clientsPage.intro.badge')}
        icon={<TeamOutlined />}
        title={t('clientsPage.intro.title')}
        description={t('clientsPage.intro.description')}
      />
      <MenuOptions
        menu_items={[
          {
            label: t('clientsPage.actions.refresh'),
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listClients(),
          },
          permissionFind([PermissionActions.clientsCreate])
            ? {
                label: t('clientsPage.actions.create'),
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => navigate(`/clients/new`),
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
        report_items={[
          {
            label: t('clientsPage.actions.clientsReport'),
            key: '1',
            icon: <FileTextOutlined />,
            onClick: () => navigate('/clients/reports/list'),
          },
          {
            label: t('clientsPage.actions.birthdays'),
            key: '2',
            icon: <FileTextOutlined />,
            onClick: () => navigate('/clients/reports/birthdays'),
          },
        ]}
      />
      <PagePanel>
        <ATable
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          scroll={{ y: 510 }}
          columns={renderColumns()}
          loading={loading}
          dataSource={clients}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default Clients;
